// src/hooks/useLabCompletedTests.ts

import {
  useCallback,
  useEffect,
  useState,
} from "react";

import {
  LAB_STORAGE_KEYS,
  loadLabStorage,
  removeLabStorage,
  saveLabStorage,
} from "../utils/labStorage";

import {
  LAB_COMPLETED_TESTS,
} from "../data/labMockData";

import type {
  CompletedTest,
  DeliveryLog,
  DeliveryMethod,
  RecipientType,
  ResultStatus,
} from "../types/lab-context.types";

interface UseLabCompletedTestsReturn {
  completedTests: CompletedTest[];

  addCompletedTest: (
    test: CompletedTest,
  ) => void;

  updateTestResultStatus: (
    testId: string,
    newStatus: ResultStatus,
  ) => void;

  addDeliveryRecord: (
    testId: string,
    method: DeliveryMethod,
    recipientType: RecipientType,
    recipient: string,
    sentBy: string,
  ) => void;

  clearCompletedTests: () => void;
  resetCompletedTests: () => void;
}

/**
 * Manages completed laboratory tests and result-delivery history.
 *
 * This hook currently persists data to localStorage.
 * Replace local state operations with API calls when the
 * laboratory backend becomes the source of truth.
 */
export const useLabCompletedTests =
  (): UseLabCompletedTestsReturn => {
    const [
      completedTests,
      setCompletedTests,
    ] = useState<CompletedTest[]>(() =>
      loadLabStorage(
        LAB_STORAGE_KEYS.COMPLETED_TESTS,
        LAB_COMPLETED_TESTS,
      ),
    );

    useEffect(() => {
      saveLabStorage(
        LAB_STORAGE_KEYS.COMPLETED_TESTS,
        completedTests,
      );
    }, [completedTests]);

    /**
     * Adds a newly completed test to the start of the list.
     *
     * Existing IDs are replaced rather than duplicated.
     */
    const addCompletedTest = useCallback(
      (
        test: CompletedTest,
      ): void => {
          throw new Error(
            "This action is currently unavailable. Your changes have not been saved."
          );
        },
      [],
    );

    /**
     * Updates the delivery state displayed on a completed test.
     */
    const updateTestResultStatus =
      useCallback(
        (
          testId: string,
          newStatus: ResultStatus,
        ): void => {
          throw new Error(
            "This action is currently unavailable. Your changes have not been saved."
          );
        },
        [],
      );

    /**
     * Adds a result-delivery attempt to a completed test.
     *
     * The record stores both:
     * - the recipient category, such as patient or doctor;
     * - the actual destination, such as an email address or phone number.
     */
    const addDeliveryRecord =
      useCallback(
        (
          testId: string,
          method: DeliveryMethod,
          recipientType: RecipientType,
          recipient: string,
          sentBy: string,
        ): void => {
          throw new Error(
            "This action is currently unavailable. Your changes have not been saved."
          );
        },
        [],
      );

    const clearCompletedTests =
      useCallback((): void => {
        removeLabStorage(
          LAB_STORAGE_KEYS.COMPLETED_TESTS,
        );

        setCompletedTests([]);
      }, []);

    const resetCompletedTests =
      useCallback((): void => {
        setCompletedTests(
          LAB_COMPLETED_TESTS,
        );
      }, []);

    return {
      completedTests,

      addCompletedTest,
      updateTestResultStatus,
      addDeliveryRecord,

      clearCompletedTests,
      resetCompletedTests,
    };
  };
